import { Icon, Label, Slider, Switch, Tooltip } from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import React from 'react';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { type Project, TRANSPARENT } from '~/common/types';
import { useCivilDrawStore } from '../../../civilDrawStoreContext';
import ColorPicker from '../ColorPicker';
import { StylingProperty } from './common';

interface LineItemProps {
  label: string;
  value: JSX.Element;
  tooltipText: string;
}

function LineItem({ label, value, tooltipText }: LineItemProps) {
  return (
    <Tooltip asChild text={tooltipText} side="left" align="start" sideOffset={5}>
      <div className="flex w-full flex-row  items-center justify-between gap-10 text-sm">
        <div className="w-1/3">{label}</div>
        <div className="flex w-2/3 justify-end">{value}</div>
      </div>
    </Tooltip>
  );
}
interface StyleProps {
  style: StylingProperty;
}

const StyleSlider = ({
  onValueChange,
  onTrackEvent,
  formatValue,
  value,
  min,
  max,
  tooltipText,
  label,
}: {
  onValueChange: (value: number) => void;
  onTrackEvent: () => void;
  formatValue: (value: number) => string;
  value: number;
  min: number;
  max: number;
  tooltipText: string;
  label: string;
}) => {
  return (
    <Slider
      label={label}
      className={'flex-row gap-10'}
      labelGroupClassName={'w-1/3'}
      labelClassName={'text-sm font-normal text-grey-50'}
      valueClassName={'font-normal text-grey-50 min-w-[3em]'}
      tooltipText={tooltipText}
      min={min}
      max={max}
      value={value}
      formatValue={formatValue}
      onValueChange={onValueChange}
      onValueCommit={onTrackEvent}
      data-testid={`civildraw-style-${label.toLowerCase().replaceAll(' ', '-')}-slider`}
    />
  );
};

const StyleSwitch = ({
  label,
  tooltipText,
  checked,
  onValueChange,
  onTrackEvent,
}: {
  label: string;
  tooltipText: string;
  checked: boolean;
  onValueChange: (value: boolean) => void;
  onTrackEvent: () => void;
}) => {
  const switchId = React.useId();

  return (
    <div className={cn('flex w-full flex-row gap-10 justify-between')}>
      <div className={cn('flex items-center shrink-0 gap-1 w-1/3')}>
        <Label htmlFor={switchId} className={cn('text-sm font-normal text-grey-50')}>
          {label}
        </Label>
        {tooltipText && (
          <Tooltip
            text={tooltipText}
            delayDuration={200}
            sideOffset={10}
            aria-label={`About ${label}`}
            triggerClassName="flex rounded-sm"
            data-testid={`civildraw-style-${label.toLowerCase().replaceAll(' ', '-')}-help`}
          >
            <Icon icon="questionMarkCircled" size={12} className="text-primary" />
          </Tooltip>
        )}
      </div>

      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={(value) => {
          onValueChange(value);
          onTrackEvent();
        }}
        data-testid={`civildraw-style-${label.toLowerCase().replaceAll(' ', '-')}-switch`}
      />
    </div>
  );
};

export default function Style({ style }: StyleProps) {
  const store = useCivilDrawStore();
  const { currentObject, currentMapId, project } = useSnapshot(store) as typeof store;
  if (!currentObject) return null;

  function updateObjectProperty(value: string | number | boolean) {
    store.updateObject({
      properties: {
        [style]: value,
      },
    });
  }

  function setPercentProperty(value: number) {
    updateObjectProperty(value / 100);
  }

  function onTrackEvent() {
    if (!currentObject) return null;
    const analyticsProject = project as unknown as Project;
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectStylingUpdated, {
      project_id: analyticsProject.id,
      organization_id: analyticsProject.organization || undefined,
      object_type: mapObjectTypeToCivildrawObjectType(currentObject.type),
      styling_property: style,
      ...(currentMapId && { map_id: currentMapId }),
    });
  }

  const props = currentObject.properties as { [key: string]: any };

  if (style === StylingProperty.ShowBorder) {
    return (
      <StyleSwitch
        label="Show Border"
        tooltipText="Toggle the object's border visibility"
        checked={props.showBorder ?? false}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.Minimized) {
    return (
      <StyleSwitch
        label="Minimized"
        tooltipText="Collapse the object to a compact view"
        checked={!!props.minimized}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.ShowTitle) {
    return (
      <StyleSwitch
        label="Show Title"
        tooltipText="Toggle the object's title visibility"
        checked={!!props.showTitle}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.Color) {
    return (
      <LineItem
        label="Color"
        tooltipText="Change the object's color"
        value={
          <ColorPicker
            label="Color"
            color={props.color ?? '#000000'}
            onChange={(color) => {
              updateObjectProperty(color);
              onTrackEvent();
            }}
          />
        }
      />
    );
  }

  if (style === StylingProperty.StrokeStyle) {
    return (
      <LineItem
        label="Line Style"
        tooltipText="Change the object's line style"
        value={
          <select
            name="obj-linetype"
            id="obj-linetype"
            data-testid="civildraw-object-line-style"
            value={props.strokeStyle}
            onChange={(e) => {
              updateObjectProperty(e.target.value);
              onTrackEvent();
            }}
            className=" cursor-pointer appearance-none rounded border-none text-center text-sm text-grey-60 transition-all hover:bg-brand-dark hover:text-white"
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
          </select>
        }
      />
    );
  }

  if (style === StylingProperty.ShowAsLeader) {
    return (
      <StyleSwitch
        label="Show as Leader"
        tooltipText="Toggle whether the arrow is shown as a leader"
        checked={!!props.showAsLeader}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.LabelAlwaysVisible) {
    return (
      <StyleSwitch
        label="Label Always Visible"
        tooltipText="Controls label visibility when zoomed out"
        checked={!!props.labelAlwaysVisible}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.ShowDescription) {
    return (
      <StyleSwitch
        label="Show Description"
        tooltipText="Toggle leader description visibility"
        checked={!!props.showDescription}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.ShowLength) {
    return (
      <StyleSwitch
        label="Show Total Length Label"
        tooltipText="Toggle object length visibility"
        checked={!!props.showLength}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.ShowArea) {
    return (
      <StyleSwitch
        label="Show Total Area Label"
        tooltipText="Toggle object area visibility"
        checked={!!props.showArea}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.ShowRadius) {
    return (
      <StyleSwitch
        label="Show Radius Label"
        tooltipText="Toggle object radius visibility"
        checked={!!props.showRadius}
        onValueChange={updateObjectProperty}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.StrokeWidth) {
    return (
      <StyleSlider
        label="Line Width"
        tooltipText="Adjust the object's line width"
        min={1}
        max={10}
        value={props.strokeWidth ?? 4}
        formatValue={(value) => `${value} px`}
        onValueChange={(value) => updateObjectProperty(value)}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.StrokeOpacity) {
    return (
      <StyleSlider
        label="Line Opacity"
        tooltipText="Adjust the object's line opacity"
        min={25}
        max={100}
        value={Math.round((props.strokeOpacity ?? 1) * 100)}
        formatValue={(value) => `${value}%`}
        onValueChange={(value) => setPercentProperty(value)}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.FillOpacity) {
    return (
      <StyleSlider
        label="Fill Opacity"
        tooltipText="Change the object's fill opacity"
        min={0}
        max={100}
        value={Math.round((props.fillOpacity ?? 0) * 100)}
        formatValue={(value) => `${value}%`}
        onValueChange={(value) => setPercentProperty(value)}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  if (style === StylingProperty.BackgroundColor) {
    return (
      <LineItem
        label="Background"
        tooltipText="Change text background color"
        value={
          <ColorPicker
            label="Background"
            color={props.backgroundColor}
            onChange={(color) => {
              updateObjectProperty(color);
              onTrackEvent();
            }}
          />
        }
      />
    );
  }

  if (style === StylingProperty.BorderColor) {
    return (
      <LineItem
        label="Border Color"
        tooltipText="Change text border color"
        value={
          <ColorPicker
            label="Border Color"
            color={props.borderColor || TRANSPARENT}
            onChange={(color) => {
              updateObjectProperty(color);
              onTrackEvent();
            }}
          />
        }
      />
    );
  }

  if (style === StylingProperty.FontSize) {
    return (
      <StyleSlider
        label="Font size"
        tooltipText="Change text font size"
        min={12}
        max={482}
        value={props.fontSize}
        formatValue={(value) => `${value} px`}
        onValueChange={(value) => updateObjectProperty(value)}
        onTrackEvent={onTrackEvent}
      />
    );
  }

  return null;
}
